import {
	wrapper,
	createApiClient,
	PaginationParams,
	Result,
	ApiError,
	ok,
} from "../base";
import DocumentDO = App.Data.Explorer.DocumentDO;
import GraphTotalDTO = App.Data.Explorer.GraphTotalDTO;
import SingleMetricResponseDTO = App.Data.Explorer.DataTable.SingleMetricResponseDTO;
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import { LaravelPaginatedCollection } from "@/types/collections";
import BookmarkDO = App.Data.BookmarkDO;
import SegmentListItemDTO = App.Data.Segments.SegmentListItemDTO;
import ExportFileType = App.Enums.ExportFileType;
import { DataStreamStatus } from "@/modules/explorer/explorerTypes";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import QuickFilterNodeValuesDTO = App.Data.Explorer.Filter.QuickFilterNodeValuesDTO;
import ReverseDimensionSearchDTO = App.Data.ReverseDimensionSearchDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import { SimpleDimensionValue } from "@/modules/explorer/components/QuickFilter/DefaultQuickFilter.vue";
import {
	GetAnnotatedEventsParams,
	GetAnnotatedEventsParamsWithDate,
} from "@/api/resources/annotated-events";
import AnnotatedEventDO = App.Data.AnnotatedEventDO;

const basePath = `explorer`;
const baseClient = createApiClient();

type DimensionParams = PaginationParams & {
	sort?: string;
	"filter[type]"?: string | object;
	"filter[is_filterable]"?: boolean;
	"filter[is_configurable_on_axis]"?: boolean;
	"filter[is_configurable_in_data_table]"?: boolean;
};

type VerticalParams = PaginationParams & {
	"filter[is_summarizable_in_segments]"?: boolean;
	"filter[include_hidden]"?: boolean;
};

export default class {
	renderImageFromChart(
		document: DocumentDO,
		apiData: any,
		graphSettings: any,
		width: number,
		height: number
	): Promise<Result<Blob, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/render-image-from-chart`,
				{
					document,
					apiData,
					graphSettings,
					width,
					height,
				},
				{ responseType: "blob" }
			)
		);
	}

	fetchTotalsForDocument(
		documentDO: DocumentDO
	): Promise<Result<GraphTotalDTO[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/fetch-totals-for-document`, documentDO)
		);
	}

	fetchTotalsForDocumentDataTable(
		documentDO: DocumentDO,
		signal?: AbortSignal
	): Promise<Result<Array<any>, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/fetch-totals-for-document-data-table`,
				documentDO,
				{ signal }
			)
		);
	}

	getDimensions(
		params: DimensionParams = {}
	): Promise<Result<App.Data.DimensionDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/get-dimensions`, { params }));
	}

	getVerticals(
		params: VerticalParams = {}
	): Promise<Result<App.Data.DocumentVerticalDTO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/get-verticals`, { params }));
	}

	getHierarchy(
		documentDO: DocumentDO | null = null,
		hierarchy_definition_id: number,
		excludedBranches: object = {},
		includeVerticalValues: boolean = false,
		signal?: AbortSignal
	): Promise<Result<QuickFilterNodeDTO, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/get-hierarchy/${hierarchy_definition_id}`,
				{
					document: documentDO,
					excludedBranches,
					includeVerticalValues,
				},
				{
					signal,
				}
			)
		);
	}

	getHierarchyNodeValues(
		documentDO: DocumentDO | null = null,
		hierarchy_definition_id: number,
		includeVerticalValues: boolean = false,
		signal?: AbortSignal
	): Promise<Result<Record<string, QuickFilterNodeValuesDTO>, ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/get-hierarchy-node-values/${hierarchy_definition_id}`,
				{
					document: documentDO,
					includeVerticalValues,
				},
				{
					signal,
				}
			)
		);
	}

	getFilterValues(
		documentDO: DocumentDO | null = null,
		dimension: DocumentDimensionDTO,
		signal?: AbortSignal
	): Promise<Result<Record<string, QuickFilterNodeValuesDTO>, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/get-filter-values`, documentDO, {
				signal,
				params: {
					dimensionId: dimension.id,
					dimensionBucketId: dimension.dimension_bucket_id,
				},
			})
		);
	}

	getBuckets(): Promise<Result<App.Data.DimensionBucketDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/get-buckets`));
	}

	getDimensionDefaults(): Promise<
		Result<App.Data.DimensionDefaultsDTO, ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/get-dimension-defaults`));
	}

	async getEmptyDocument(
		uuid: string | null
	): Promise<Result<DocumentDO, ApiError>> {
		const res = await wrapper<DocumentDO>(
			baseClient.get(`${basePath}/get-empty-document/${uuid}`)
		);
		if (res.isOk()) {
			return ok(this.parseSingleMetricInDocument(res.value));
		}

		return res;
	}

	getDefaultDataTable(): Promise<
		Result<App.Data.Explorer.DataTableDO, ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/get-default-data-table`));
	}

	getDefaultFilters(): Promise<
		Result<App.Data.Explorer.Filter.ActiveFilterDTO[], ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/get-default-filters`));
	}

	getSpecificationForHierarchy(
		dimension: App.Data.DimensionDO
	): Promise<
		Result<App.Data.Explorer.Filter.FilterSpecificationDTO[], ApiError>
	> {
		return wrapper(
			baseClient.get(
				`${basePath}/get-specification-for-hierarchy/${dimension.id}`
			)
		);
	}

	getValuesForDimensionAndSpecification(
		dimension_id: number,
		specification_id: number | null
	): Promise<Result<App.Data.Explorer.Filter.FilterValueDTO[], ApiError>> {
		return wrapper(
			baseClient.get(
				`${basePath}/get-values-for-dimension-and-specification/${dimension_id}` +
					(specification_id ? "/" + specification_id : "")
			)
		);
	}

	getNodesForHierarchy(
		specification_id: number
	): Promise<Result<App.Data.Explorer.Filter.FilterValueDTO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/get-nodes-for-hierarchy/${specification_id}`)
		);
	}

	async getDocumentHistory(
		baseDocument?: string
	): Promise<Result<DocumentDO[], ApiError>> {
		const result = await wrapper<DocumentDO[]>(
			baseClient.get(`${basePath}/get-document-history`, {
				params: {
					"base-document": baseDocument,
				},
			})
		);
		if (result.isOk()) {
			return ok(
				result.value.map((doc) => this.parseSingleMetricInDocument(doc))
			);
		}

		return result;
	}

	getFilterObjectAfterHierarchyClick(
		clickedDimension: App.Data.DimensionDO,
		activeFilterOnDimension: App.Data.Explorer.Filter.ActiveFilterDTO | null,
		clickedValue: string,
		direction: string
	): Promise<Result<App.Data.Explorer.Filter.ActiveFilterDTO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/get-filter-object-after-hierarchy-click`, {
				params: {
					clickedDimension,
					activeFilterOnDimension,
					clickedValue,
					direction,
				},
			})
		);
	}

	getDocumentFromDatabase(
		uuid: string,
		direction: string
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(
				`${basePath}/get-document-from-database/${uuid.replace(
					"DB-",
					""
				)}/${direction}`
			)
		);
	}

	fetchDataForDocument(
		appDocument: DocumentDO,
		routePrefix: string = "/",
		signal?: AbortSignal
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(`${routePrefix}fetchDataForDocument`, appDocument, {
				signal,
			})
		);
	}

	fetchDataForDocumentDataTable(
		appDocument: DocumentDO,
		routePrefix: string = "/",
		signal?: AbortSignal
	): Promise<Result<{ data: DataTableResponseDTO[] }, ApiError>> {
		return wrapper(
			baseClient.post(
				`${routePrefix}fetchDataForDocumentDataTable`,
				appDocument,
				{ signal }
			)
		);
	}

	logDocument(
		appDocument: DocumentDO,
		routePrefix: string = "/",
		signal?: AbortSignal
	): Promise<Result<{ ok: boolean }, ApiError>> {
		return wrapper(
			baseClient.post(`${routePrefix}log-document`, appDocument, { signal })
		);
	}

	fetchDataForDocumentSingleMetric(
		appDocument: DocumentDO,
		routePrefix: string = "/",
		signal?: AbortSignal
	): Promise<Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>> {
		return wrapper(
			baseClient.post(
				`${routePrefix}fetchDataForDocumentSingleMetric`,
				appDocument,
				{ signal }
			)
		).then(
			(result: Result<{ singleMetric: SingleMetricResponseDTO }, ApiError>) => {
				if (result.isOk()) {
					if (result.value.singleMetric) {
						if (typeof result.value.singleMetric?.data === "number") {
							return result;
						}

						result.value.singleMetric.data =
							result.value.singleMetric?.data === "-Inf"
								? -Infinity
								: +Infinity;
					}
				}

				return result;
			}
		);
	}

	downloadUnderlyingData(
		appDocument: DocumentDO,
		exportFileType: ExportFileType
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/get-underlying-data`, appDocument, {
				responseType: "blob",
				params: {
					format: exportFileType,
				},
			})
		);
	}

	downloadGraphData(
		appDocument: DocumentDO,
		exportFileType: ExportFileType
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/download-graph-data`, appDocument, {
				responseType: "blob",
				params: {
					format: exportFileType,
				},
			})
		);
	}

	fetchRawDataForDocument(
		appDocument: DocumentDO
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/fetchDataForDocument?rawData=true`,
				appDocument
			)
		);
	}

	fetchBaseDataForDocument(
		appDocument: DocumentDO
	): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(
				`${basePath}/fetchDataForDocument?baseData=true`,
				appDocument
			)
		);
	}

	validateDocument(appDocument: any): Promise<Result<[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/validate-document`, appDocument)
		);
	}

	fakeDataTableResponse(): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/fake-data-table-response`));
	}

	private parseSingleMetricInDocument(document: DocumentDO): DocumentDO {
		// document.single_metric = SingleMetric.fromData(document.single_metric);

		return document;
	}

	getStoredDocument(
		documentUuid: string
	): Promise<Result<App.Data.StoredDocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/get-stored-document/${documentUuid}`)
		);
	}

	getBookmarks(): Promise<Result<App.Data.BookmarkDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/bookmarks`));
	}

	saveBookmarks(bookmarks: BookmarkDO[]): Promise<Result<[], ApiError>> {
		return wrapper(baseClient.post(`${basePath}/bookmarks`, bookmarks));
	}

	downloadDataTable(
		document: DocumentDO,
		format: ExportFileType
	): Promise<Result<string, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/download-data-table`, document, {
				responseType: "blob",
				params: {
					format: format,
				},
			})
		);
	}

	indexSegments(params: {
		page: number;
		itemsPerPage: number;
		sortBy: { key: string; order: "asc" | "desc" }[];
	}): Promise<
		Result<LaravelPaginatedCollection<SegmentListItemDTO>, ApiError>
	> {
		let sortBy = undefined;
		if (params.sortBy.length > 0) {
			sortBy =
				params.sortBy[0].order === "desc"
					? `-${params.sortBy[0].key}`
					: params.sortBy[0].key;
		}
		return wrapper(
			baseClient.get(`${basePath}/index-segments`, {
				params: {
					page: params.page,
					per_page: params.itemsPerPage,
					sort: sortBy,
				},
			})
		);
	}

	getDataStreamStatus(
		document_uuid: string
	): Promise<Result<DataStreamStatus[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/get-data-status`, {
				params: { document_uuid },
			})
		);
	}

	reverseDimensionSearch(
		searchQuery: string,
		limit: number = 50
	): Promise<Result<ReverseDimensionSearchDTO[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/reverse-dimension-search`, {
				params: { searchQuery, limit },
			})
		);
	}

	getBucketValuesForQuickFilter(
		bucketId: number,
		skip: number,
		take: number
	): Promise<Result<SimpleDimensionValue[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/bucketed/${bucketId}/values-quick-filter`, {
				params: {
					skip,
					take,
				},
			})
		);
	}

	getDimensionValuesForQuickFilter(
		dimensionId: number,
		skip: number,
		take: number
	): Promise<Result<SimpleDimensionValue[], ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/nominal/${dimensionId}/values-quick-filter`, {
				params: {
					skip,
					take,
				},
			})
		);
	}

	getAnnotatedEvents(
		params: GetAnnotatedEventsParams | GetAnnotatedEventsParamsWithDate = {}
	): Promise<Result<AnnotatedEventDO[], ApiError>> {
		const query = {
			"filter[created_by_user_id]": params.created_by_user_id,
			"filter[applies_to_subject_date]": params.applies_to_subject_date,
			"filter[applies_to_measure_date]": params.applies_to_measure_date,
			"filter[category_id]": params.category_ids
				? params.category_ids.map((categoryId) =>
						categoryId === null ? "null" : categoryId
				  )
				: undefined,
		};

		if ("from" in params) {
			query["filter[in_range]"] =
				params.from.toISOString() + "," + params.to.toISOString();
		}

		return wrapper(
			baseClient.get(`${basePath}/annotated-events`, { params: query })
		);
	}

	/**
	 * Persists the configuration so the returned uuid is guaranteed to resolve,
	 * even when it has not been through a data fetch yet.
	 */
	generateShareLink(
		appDocument: DocumentDO
	): Promise<Result<{ uuid: string }, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/share-link`, appDocument));
	}

	getShareLinkDocument(
		shareLinkUuid: string
	): Promise<Result<DocumentDO, ApiError>> {
		return wrapper(
			baseClient.get(`${basePath}/share-link/document/${shareLinkUuid}`)
		);
	}
}
